import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HowToUseSection() {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  return (
    <section id="how-to-use" className="py-12 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Nanum Myeongjo', serif" }}
          >
            서명은 어떻게 활용되나요?
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600">
            여러분의 서명이 사법정의 회복으로 이어지는 과정
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-2xl rounded-bl-2xl p-6 md:p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center text-amber-600 mt-0.5 shrink-0">
              <i className="ri-scales-3-line text-2xl"></i>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-snug">
              사법정의 회복을 위한 국민의 목소리
            </h3>
          </div>

          <div className="space-y-4 text-sm md:text-base text-gray-700 leading-relaxed pl-11">
            <p>
              저희는 현행 사법 시스템의 문제의식을 갖고, <strong className="text-gray-900">사법정의 회복이 필요하다고 생각하는 국민들이 적지 않다</strong>는 사실을 지속적으로 알리고자 합니다. 이를 통해 제도권 정치가 국민적 요구를 외면하지 못하도록 공론을 형성해 나갈 계획입니다.
            </p>
            <p>
              일정한 참여 규모에 도달하면, 국회의원들을 직접 찾아가 국민 다수의 뜻을 전달하고, 이를 바탕으로{' '}
              <strong className="text-amber-700">'의원소개청원'</strong> 제도를 통해 실질적인 입법으로 이어가고자 합니다.
            </p>
            <p>
              이 운동이 실제 입법으로 곧바로 이어질지는 단정할 수 없으나, 서명 참여가 꾸준히 확대되는 과정 자체가 국민적 관심을 높이고 사법정의에 대한 공감대를 형성하는 의미 있는 계기가 될 것이라 생각합니다.
            </p>
            <p>
              만약 참여 인원이 크게 확대된다면, 어느 정당이든 국민적 요구를 받아들일 수밖에 없을 것으로 기대합니다.
            </p>
          </div>
        </div>

        {/* Petition MP Notice */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 md:p-6 mb-6 flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full shrink-0 mt-0.5">
            <i className="ri-government-line text-red-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm md:text-base font-bold text-red-700 mb-1">
              청원 의원 섭외 진행 중
            </p>
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              현재 청원을 받아줄 국회의원을 섭외 중에 있습니다. 의원이 확정되는 즉시 본 홈페이지를 통해 공유드릴 예정입니다. 많은 관심과 응원 부탁드립니다.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            {
              icon: 'ri-pencil-line',
              step: '01',
              title: '서명 참여',
              desc: '사법정의 회복을 원하는 국민 누구나 서명에 참여할 수 있습니다.',
            },
            {
              icon: 'ri-user-voice-line',
              step: '02',
              title: '의원 전달',
              desc: '충분한 서명이 모이면 국회의원을 직접 찾아가 국민의 뜻을 전달합니다.',
            },
            {
              icon: 'ri-file-list-3-line',
              step: '03',
              title: '의원소개청원',
              desc: '의원소개청원 제도를 통해 실질적인 입법 절차로 이어갑니다.',
            },
          ].map((item) => (
            <div
              key={item.step}
              className="bg-gray-50 rounded-2xl p-5 flex flex-col items-center text-center border border-gray-100 hover:border-red-200 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-3">
                <i className={`${item.icon} text-red-600 text-xl`}></i>
              </div>
              <span className="text-xs font-bold text-red-500 mb-1">STEP {item.step}</span>
              <p className="text-sm md:text-base font-bold text-gray-900 mb-2">{item.title}</p>
              <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Quote Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 md:p-8 text-center shadow-lg">
          <i className="ri-double-quotes-l text-white/40 text-4xl mb-2 block"></i>
          <p className="text-base md:text-lg font-bold text-white leading-relaxed">
            주권자인 국민 다수가 뜻을 모은다면,<br />
            정치권 역시 그 요구에 응답하지 않을 수 없을 것입니다.
          </p>
        </div>

        {/* CTA 버튼 */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/sign')}
            className="inline-flex items-center gap-2 px-10 py-4 bg-red-600 text-white rounded-full text-base font-bold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap shadow-xl"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-pen-nib-line text-lg"></i>
            </span>
            지금 바로 서명하기
          </button>
        </div>
      </div>
    </section>
  );
}
